/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import ClassicEditorBase from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import InlineEditorBase from '@ckeditor/ckeditor5-editor-inline/src/inlineeditor';

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import UploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder';
import EasyImage from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import Link from '@ckeditor/ckeditor5-link/src/link';
import List from '@ckeditor/ckeditor5-list/src/list';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation';
import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices';
import SourceEditing from "@ckeditor/ckeditor5-source-editing/src/sourceediting";
import Markdown from "@ckeditor/ckeditor5-markdown-gfm/src/markdown";
import Mention from "@ckeditor/ckeditor5-mention/src/mention";
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import RemoveFormat from "@ckeditor/ckeditor5-remove-format/src/removeformat";
import LinkImage from "@ckeditor/ckeditor5-link/src/linkimage";
import PastePlainText from "@ckeditor/ckeditor5-clipboard/src/pasteplaintext";
import SpecialCharacters from '@ckeditor/ckeditor5-special-characters/src/specialcharacters';
import SpecialCharactersCurrency from '@ckeditor/ckeditor5-special-characters/src/specialcharacterscurrency';
import SpecialCharactersText from '@ckeditor/ckeditor5-special-characters/src/specialcharactersmathematical';
import Clipboard from '@ckeditor/ckeditor5-clipboard/src/clipboard';
import PasteFromMarkdownExperimental from '@ckeditor/ckeditor5-markdown-gfm/src/pastefrommarkdownexperimental';
import CodeBlock from '@ckeditor/ckeditor5-code-block/src/codeblock';
import {Code, Strikethrough, Underline} from "@ckeditor/ckeditor5-basic-styles";
import FullScreen from '@pikulinpw/ckeditor5-fullscreen';
import ResizableHeight from '@pikulinpw/ckeditor5-resizableheight';

// import SpecialCharactersEmoji from './specialcharactersemoji.js';

class ClassicEditor extends ClassicEditorBase {}
class InlineEditor extends InlineEditorBase {}

function SpecialCharactersEmoji( editor ) {
    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Smiley Face Emojis', [
        { character: '🙂', title: 'Slightly smiling face' },
        { character: '😀', title: 'Smiling face' },
        { character: '😃', title: 'Smiling face with big eyes' },
        { character: '😄', title: 'Smiling face with smiling eyes' },
        { character: '😁', title: 'Beaming face with smiling eyes' },
        { character: '😅', title: 'Smiling face with tears' },
        { character: '😆', title: 'Grinning face' },
        { character: '🤣', title: 'Rolling on the floor laughing' },
        { character: '😂', title: 'Lauging with tears' },
        { character: '🙃', title: 'Upside down face' },
        { character: '😉', title: 'Winking face' },
        { character: '😊', title: 'Smiling face with smiling eyes' },
        { character: '😇', title: 'Smiling face with halo' },
        { character: '😎', title: 'Smiling face with sunglasses' },
        { character: '🤓', title: 'Nerdy face' },
        { character: '🧐', title: 'Face with monocle' },
        { character: '🥳', title: 'Partying face' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Emotional Faces', [
        { character: '🥰', title: 'Smiling face with hearts' },
        { character: '😍', title: 'Smiling face with heart eyes' },
        { character: '🤩', title: 'Star-struck' },
        { character: '😘', title: 'Face blowing kiss' },
        { character: '😗', title: 'Kissing face' },
        { character: '☺', title: 'Smiling face' },
        { character: '😚', title: 'Kissing face with closed eyes' },
        { character: '😙', title: 'Kissng face with smiling eyes' },
        { character: '🥲', title: 'Smiling face with tears' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Faces with Tongue Emojis', [
        { character: '😋', title: 'Yummy face' },
        { character: '😛', title: 'Face with tongue' },
        { character: '😜', title: 'WInking face with tongue' },
        { character: '🤪', title: 'Zanny face' },
        { character: '😝', title: 'Squinting face with tongue' },
        { character: '🤑', title: 'Money face with money tongue' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Faces with Hands Emojis', [
        { character: '🤗', title: 'Hugs' },
        { character: '🤭', title: 'Face with hand in mouth' },
        { character: '🤫', title: 'Shushing face' },
        { character: '🤔', title: 'Thinkin face' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Neutral Faces Emojis', [
        { character: '😐', title: 'Neutral face' },
        { character: '🤐', title: 'Zipped mouth' },
        { character: '🤨', title: 'Face with raised eyebrow' },
        { character: '😑', title: 'Expressionless face' },
        { character: '😶', title: 'Face with no mouth' },
        { character: '😏', title: 'Smirking face' },
        { character: '😒', title: 'Unamused face' },
        { character: '🙄', title: 'Face with rolling eyes' },
        { character: '😬', title: 'Grimacing face' },
        { character: '😮‍💨', title: 'Grimacing face' },
        { character: '🤥', title: 'Lying face' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Sleepy Faces Emojis', [
        { character: '😪', title: 'Sleepy face' },
        { character: '😴', title: 'Sleeping face' },
        { character: '😌', title: 'Relieved face' },
        { character: '😔', title: 'Pensive face' },
        { character: '🤤', title: 'Drooling face' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Sick Faces Emojis', [
        { character: '😷', title: 'Face with mask' },
        { character: '🤒', title: 'Face with thermometer' },
        { character: '🤕', title: 'Face with bandage' },
        { character: '🤢', title: 'Nauseous face' },
        { character: '🤮', title: 'Vomiting face' },
        { character: '🤧', title: 'Sneezing face' },
        { character: '🥵', title: 'Hot face' },
        { character: '🥶', title: 'Cold face' },
        { character: '🥴', title: 'Woozy face' },
        { character: '😵', title: 'Face with crossed-out face' },
        { character: '🤯', title: 'Face with exploding head' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Concerned Faces Emojis', [
        { character: '😕', title: 'Confused face' },
        { character: '😟', title: 'Worried face' },
        { character: '🙁', title: 'Slightly frowning face' },
        { character: '☹', title: 'Frowning face' },
        { character: '😮', title: 'Face with open mouth' },
        { character: '😯', title: 'Hushed face' },
        { character: '😲', title: 'Astonished face' },
        { character: '😳', title: 'Flushed face' },
        { character: '🥺', title: 'Begging face' },
        { character: '😦', title: 'Frowning face with open mouth' },
        { character: '😧', title: 'Angushed face' },
        { character: '😨', title: 'Fearful face' },
        { character: '😰', title: 'Anxious face with sweat' },
        { character: '😥', title: 'Sad but relieved face' },
        { character: '😢', title: 'Crying face' },
        { character: '😭', title: 'Loudly crying face' },
        { character: '😱', title: 'Screaming face' },
        { character: '😖', title: 'Confounded face' },
        { character: '😣', title: 'Persevering face' },
        { character: '😞', title: 'Disapointed face' },
        { character: '😓', title: 'Downcast face with sweat' },
        { character: '😩', title: 'Weary face' },
        { character: '😫', title: 'Tired face' },
        { character: '🥱', title: 'Yawning face' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Negative Faces', [
        { character: '😤', title: 'Face with steam' },
        { character: '😡', title: 'Pouting face' },
        { character: '😠', title: 'Angry face' },
        { character: '🤬', title: 'Face with symbols on mouth' },
        { character: '😈', title: 'Smiling face with horns' },
        { character: '👿', title: 'Angry face with horns' },
        { character: '💀', title: 'Skull' },
        { character: '☠', title: 'Skull and cross-bone' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Costume Faces Emojis', [
        { character: '💩', title: 'Pile of poo' },
        { character: '🤡', title: 'Clown' },
        { character: '👹', title: 'Ogre' },
        { character: '👺', title: 'Goblin' },
        { character: '👻', title: 'Ghost' },
        { character: '👽', title: 'Alien' },
        { character: '👾', title: 'Alien monster' },
        { character: '🤖', title: 'Robot' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Cat Faces Emojis', [
        { character: '😺', title: 'Grinnig cat' },
        { character: '😸', title: 'Grinning cat with smiling eyes' },
        { character: '😹', title: 'Grinning cat with tears' },
        { character: '😻', title: 'Smiling cat with heart eyes' },
        { character: '😼', title: 'Cat with wry smile' },
        { character: '😽', title: 'Kissing cat' },
        { character: '🙀', title: 'Weary cat' },
        { character: '😿', title: 'Crying cat' },
        { character: '😾', title: 'Pouting cat' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Monkey Faces Emojis', [
        { character: '🙈', title: 'See no evil monkey' },
        { character: '🙉', title: 'Hear no evil monkey' },
        { character: '🙊', title: 'Speak no evil monkey' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Emotion Emojis', [
        { character: '💋', title: 'Kiss' },
        { character: '💌', title: 'Love letter' },
        { character: '💘', title: 'Heart with arrow' },
        { character: '💝', title: 'HEart with ribbon' },
        { character: '💖', title: 'Sparking heart' },
        { character: '💗', title: 'Growing heart' },
        { character: '💓', title: 'Beating heart' },
        { character: '💞', title: 'Revolving heart' },
        { character: '💕', title: 'Two hearts' },
        { character: '💟', title: 'Heart decoration' },
        { character: '❣', title: 'Heart exclamation' },
        { character: '💔', title: 'Broken heart' },
        { character: '❤️‍🔥', title: 'Heart on fire' },
        { character: '❤️‍🩹', title: 'Mending heart' },
        { character: '❤', title: 'Red heart' },
        { character: '🧡', title: 'Orange heart' },
        { character: '💛', title: 'Yellow heart' },
        { character: '💚', title: 'Green heart' },
        { character: '💙', title: 'Blue heart' },
        { character: '💜', title: 'Purple heart' },
        { character: '🤎', title: 'Brown heart' },
        { character: '🖤', title: 'Black heart' },
        { character: '🤍', title: 'White heart' },
        { character: '💯', title: 'Hundred(correct)' },
        { character: '💢', title: 'Anger' },
        { character: '💥', title: 'collision' },
        { character: '💫', title: 'Dizzy' },
        { character: '💦', title: 'Sweat droplets' },
        { character: '💨', title: 'Dashing away' },
        { character: '🕳', title: 'Hole' },
        { character: '💣', title: 'Bomb' },
        { character: '💬', title: 'Message baloon' },
        { character: '👁️‍🗨️', title: 'Eye in speech bubble' },
        { character: '🗨', title: 'Left speech bubble' },
        { character: '🗯', title: 'Anger bubble' },
        { character: '💭', title: 'Thought baloon' },
        { character: '💤', title: 'zzz' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Hands and other Body Parts Emojis', [
        { character: '👋', title: 'Waving hand' },
        { character: '🤚', title: 'Raised back of hand' },
        { character: '🖐', title: 'Hands with splayed finger' },
        { character: '✋', title: 'Raised hand' },
        { character: '🖖', title: 'Vulcan salute' },
        { character: '👌', title: 'Ok' },
        { character: '🤌', title: 'Pinched fingers' },
        { character: '🤏', title: 'Pinched hand' },
        { character: '✌', title: 'Victory hand' },
        { character: '🤞', title: 'Crossed fingers' },
        { character: '🤟', title: 'Love you' },
        { character: '🤘', title: 'Horn sign' },
        { character: '🤙', title: 'Call me hand' },
        { character: '👈', title: 'Index finger pointing left' },
        { character: '👉', title: 'Index finger pointing right' },
        { character: '👆', title: 'Index finger pointing up' },
        { character: '👇', title: 'Index finger pointing down' },
        { character: '🖕', title: 'Middle finger' },
        { character: '☝', title: 'Forehand Index finger pointing up' },
        { character: '🫵', title: 'Index finger pointing at viewer' },
        { character: '👍', title: 'Thumbs up' },
        { character: '👎', title: 'Thumbs down' },
        { character: '✊', title: 'Raised fist' },
        { character: '👊', title: 'Fist' },
        { character: '🤛', title: 'Left facing fist' },
        { character: '🤜', title: 'Right facing fist' },
        { character: '👏', title: 'Clapping hands' },
        { character: '🙌', title: 'Raised hands' },
        { character: '👐', title: 'OPen hands' },
        { character: '🤲', title: 'Palms together hands' },
        { character: '🤝', title: 'Handshake' },
        { character: '🙏', title: 'Praying hands' },
        { character: '✍', title: 'Writing hands' },
        { character: '💅', title: 'Nail polish' },
        { character: '🤳', title: 'Selfie hand' },
        { character: '💪', title: 'Flexed biceps' },
        { character: '🦾', title: 'MEchanical arm' },
        { character: '🦵', title: 'Leg' },
        { character: '🦿', title: 'Mechanical leg' },
        { character: '🦶', title: 'Foot' },
        { character: '👂', title: 'Ear' },
        { character: '🦻', title: 'Ear with earing aid' },
        { character: '👃', title: 'Nose' },
        { character: '🧠', title: 'Brain' },
        { character: '👣', title: 'Footprint' },
        { character: '🫀', title: 'MEchanical heart' },
        { character: '🫁', title: 'Lungs' },
        { character: '🦷', title: 'Tooth' },
        { character: '🦴', title: 'Bone' },
        { character: '👀', title: 'Eyes' },
        { character: '👁', title: 'Eye' },
        { character: '👅', title: 'Nose' },
        { character: '👄', title: 'Mouth' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Person Emojis', [
        { character: '🧑', title: 'Person' },
        { character: '👶', title: 'Baby' },
        { character: '🧒', title: 'Child' },
        { character: '👦', title: 'Boy' },
        { character: '👧', title: 'Girl' },
        { character: '👱', title: 'Person with blonde hair' },
        { character: '👨', title: 'Man' },
        { character: '🧔', title: 'Bearded person' },
        { character: '🧔‍♂‍', title: 'Bearded man' },
        { character: '🧔‍♀‍', title: 'Bearded woman' },
        { character: '👨‍🦰', title: 'MAn with red hair' },
        { character: '👨‍🦱', title: 'Man with curly hair' },
        { character: '👨‍🦳', title: 'Man with white hair' },
        { character: '👨‍🦲', title: 'Bald man' },
        { character: '👩', title: 'Woman' },
        { character: '👩‍🦰', title: 'Woman with red hair' },
        { character: '👩‍🦱', title: 'Woman with curly hair' },
        { character: '👩‍🦳', title: 'Woman with white hair' },
        { character: '👩‍🦲', title: 'Bald woman' },
        { character: '👱‍♀‍', title: 'Woman with blode hair' },
        { character: '👱‍♂‍', title: 'Man with blonde hair' },
        { character: '🧓', title: 'Old person' },
        { character: '👴', title: 'Old man' },
        { character: '👵', title: 'Old woman' },
        { character: '🙍', title: 'Person frowning' },
        { character: '🙍‍♂‍', title: 'Man frowning' },
        { character: '🙍‍♀‍', title: 'Woman frowning' },
        { character: '🙎', title: 'Person pouting' },
        { character: '🙎‍♂‍', title: 'Man pouting' },
        { character: '🙎‍♀‍', title: 'Woman pouting' },
        { character: '🙅', title: 'Person gesturing no' },
        { character: '🙅‍♂‍', title: 'Man gesturing no' },
        { character: '🙅‍♀‍', title: 'Woman gesturing no' },
        { character: '🙆', title: 'Person stretching' },
        { character: '🙆‍♂‍', title: 'Man stretching' },
        { character: '🙆‍♀‍', title: 'Woman stretching' },
        { character: '💁', title: 'Person tipping hand' },
        { character: '💁‍♂‍', title: 'Man tipping hand' },
        { character: '💁‍♀‍', title: 'Woman tipping hand' },
        { character: '🙋', title: 'Person rainsing hand' },
        { character: '🙋‍♂‍', title: 'Man raising hand' },
        { character: '🙋‍♀‍', title: 'Woman raisning hand' },
        { character: '🧏', title: 'Deaf person' },
        { character: '🧏‍♂‍', title: 'Deaf man' },
        { character: '🧏‍♀‍', title: 'Deaf woman' },
        { character: '🙇', title: 'Person bowing' },
        { character: '🙇‍♂‍', title: 'Man bowing' },
        { character: '🙇‍♀‍', title: 'Woman bowing' },
        { character: '🤦', title: 'Person facepalming' },
        { character: '🤦‍♂‍', title: 'Man facepalming' },
        { character: '🤦‍♀‍', title: 'Woman facepalming' },
        { character: '🤷', title: 'Person shrugging' },
        { character: '🤷‍♂‍', title: 'Man shrugging' },
        { character: '🤷‍♀‍', title: 'Woman shrugging' },
        { character: '🧑‍⚕‍', title: 'Health worker' },
        { character: '👨‍⚕‍', title: 'Man health worker' },
        { character: '👩‍⚕‍', title: 'Woman health worker' },
        { character: '🧑‍🎓', title: 'Student' },
        { character: '👨‍🎓', title: 'Man student' },
        { character: '🧑‍🏫', title: 'Teacher' },
        { character: '👨‍🏫', title: 'Man teacher' },
        { character: '👩‍🏫', title: 'Woman teacher' },
        { character: '🧑‍⚖‍', title: 'Judge' },
        { character: '👨‍⚖‍', title: 'Man judge' },
        { character: '👩‍⚖‍', title: 'Woman judge' },
        { character: '🧑‍🌾', title: 'Farmer' },
        { character: '👨‍🌾', title: 'Man farmer' },
        { character: '👩‍🌾', title: 'Woman farmer' },
        { character: '🧑‍🍳', title: 'Cook' },
        { character: '👨‍🍳', title: 'Man cook' },
        { character: '👩‍🍳', title: 'Woman cook' },
        { character: '🧑‍🔧', title: 'Mechanic' },
        { character: '👨‍🔧', title: 'Man mechanic' },
        { character: '👩‍🔧', title: 'Woman mechanic' },
        { character: '🧑‍🏭', title: 'Factory worker' },
        { character: '👨‍🏭', title: 'Man factory worker' },
        { character: '👩‍🏭', title: 'Woman factory worker' },
        { character: '🧑‍💼', title: 'Office worker' },
        { character: '👨‍💼', title: 'Man office worker' },
        { character: '👩‍💼', title: 'Woma office worker' },
        { character: '🧑‍🔬', title: 'Scientist' },
        { character: '👨‍🔬', title: 'Man scientist' },
        { character: '👩‍🔬', title: 'Woman scientist' },
        { character: '🧑‍💻', title: 'Technologist' },
        { character: '👨‍💻', title: 'Man texhnologist' },
        { character: '👩‍💻', title: 'Woman technologist' },
        { character: '🧑‍🎤', title: 'Singer' },
        { character: '👨‍🎤', title: 'Man singer' },
        { character: '👩‍🎤', title: 'Woman singer' },
        { character: '🧑‍🎨', title: 'Artist' },
        { character: '👨‍🎨', title: 'Man artist' },
        { character: '👩‍🎨', title: 'Woman artist' },
        { character: '🧑‍✈‍', title: 'Pilot' },
        { character: '👨‍✈‍', title: 'Man pilot' },
        { character: '👩‍✈‍', title: 'Woman pilot' },
        { character: '🧑‍🚀', title: 'Astronaut' },
        { character: '👨‍🚀', title: 'Man astronaut' },
        { character: '👩‍🚀', title: 'Woman astronaut' },
        { character: '🧑‍🚒', title: 'Firefighter' },
        { character: '👨‍🚒', title: 'Man firefighter' },
        { character: '👩‍🚒', title: 'Woman firefighter' },
        { character: '👮', title: 'Police' },
        { character: '👮‍♂‍', title: 'Policeman' },
        { character: '👮‍♀‍', title: 'Policewoman' },
        { character: '🕵', title: 'Detective' },
        { character: '🕵️‍♂‍', title: 'Man detective' },
        { character: '🕵️‍♀‍', title: 'Woman detective' },
        { character: '💂', title: 'Guard' },
        { character: '💂‍♂‍', title: 'Man guard' },
        { character: '💂‍♀‍', title: 'Woman guard' },
        { character: '🥷', title: 'Ninja' },
        { character: '👷', title: 'Construction worker' },
        { character: '👷‍♂‍', title: 'Man construction worker' },
        { character: '👷‍♀‍', title: 'Woman construction worker' },
        { character: '🤴', title: 'Prince' },
        { character: '👸', title: 'Princess' },
        { character: '👳', title: 'Person wearing turban' },
        { character: '👳‍♂‍', title: 'Man wearing turban' },
        { character: '👳‍♀‍', title: 'Woman wearing turban' },
        { character: '👲', title: 'Person with skullcap' },
        { character: '🧕', title: 'Woman with headscaff' },
        { character: '🤵', title: 'Person in tuxedo' },
        { character: '🤵‍♂‍', title: 'Man in tuxedo' },
        { character: '🤵‍♀‍', title: 'Woman in tuxedo' },
        { character: '👰', title: 'Person in veil' },
        { character: '👰‍♂‍', title: 'Man in veil' },
        { character: '👰‍♀‍', title: 'Woman in veil' },
        { character: '🤰', title: 'Pregnant woman' },
        { character: '🤱', title: 'Breast-feeding' },
        { character: '🧑‍🍼', title: 'Person feeding baby' },
        { character: '👩‍🍼', title: 'WOman feeding baby' },
        { character: '👨‍🍼', title: 'Man feeding baby' },
        { character: '👼', title: 'Baby angel' },
        { character: '🎅', title: 'Santa claus' },
        { character: '🤶', title: 'Mrs Claus' },
        { character: '🧑‍🎄', title: 'Mx Claus' },
        { character: '🦸', title: 'Superhero' },
        { character: '🦸‍♂‍', title: 'Man superhero' },
        { character: '🦸‍♀‍', title: 'Woman superhero' },
        { character: '🦹', title: 'Supervillain' },
        { character: '🦹‍♂‍', title: 'Man superhero' },
        { character: '🦹‍♀‍', title: 'Woman superhero' },
        { character: '🧙', title: 'Mage' },
        { character: '🧙‍♂‍', title: 'Man mage' },
        { character: '🧙‍♀‍', title: 'Woman mage' },
        { character: '🧚', title: 'Fairy' },
        { character: '🧚‍♂‍', title: 'Man fairy' },
        { character: '🧚‍♀‍', title: 'Woman fairy' },
        { character: '🧛', title: 'Vampire' },
        { character: '🧛‍♂‍', title: 'Man vampire' },
        { character: '🧛‍♀‍', title: 'Woman vampire' },
        { character: '🧜', title: 'Merperson' },
        { character: '🧜‍♂‍', title: 'Merman' },
        { character: '🧜‍♀‍', title: 'Mermaid' },
        { character: '🧝', title: 'Elf' },
        { character: '🧝‍♂‍', title: 'Man elf' },
        { character: '🧝‍♀‍', title: 'Woman elf' },
        { character: '🧞', title: 'Genie' },
        { character: '🧞‍♂‍', title: 'Man genie' },
        { character: '🧞‍♀‍', title: 'Woman genie' },
        { character: '🧟', title: 'Zombie' },
        { character: '🧟‍♂‍', title: 'Man zombie' },
        { character: '🧟‍♀‍', title: 'Woman zombie' },
        { character: '💆', title: 'Person getting massage' },
        { character: '💆‍♂‍', title: 'Man getting massage' },
        { character: '💆‍♀‍', title: 'Woman getting massage' },
        { character: '💇', title: 'Person getting haircut' },
        { character: '💇‍♂‍', title: 'Man getting haircut' },
        { character: '💇‍♀‍', title: 'Woman getting haircut' },
        { character: '🚶', title: 'Person walking' },
        { character: '🚶‍♂‍', title: 'Man walking' },
        { character: '🚶‍♀‍', title: 'Woman walking' },
        { character: '🧍', title: 'Person standing' },
        { character: '🧍‍♂‍', title: 'Man standing' },
        { character: '🧍‍♀‍', title: 'Woman standing' },
        { character: '🧎', title: 'Person kneeling' },
        { character: '🧎‍♂‍', title: 'Man kneeling' },
        { character: '🧎‍♀‍', title: 'Woman kneeling' },
        { character: '🧑‍🦯', title: 'PErson with walking stick' },
        { character: '👨‍🦯', title: 'Man with walking stick' },
        { character: '👩‍🦯', title: 'Woman with walking stick' },
        { character: '🧑‍🦼', title: 'Person in motorized wheelchair' },
        { character: '👨‍🦼', title: 'Man in motorized wheelchair' },
        { character: '👩‍🦼', title: 'Womain in motorized wheelchair' },
        { character: '🧑‍🦽', title: 'Person in manual wheelchair' },
        { character: '👨‍🦽', title: 'Man in manual wheelchair' },
        { character: '👩‍🦽', title: 'Womaan in motorized wheelchair' },
        { character: '🏃', title: 'Person sprinting' },
        { character: '🏃‍♂‍', title: 'Man sprinting' },
        { character: '🏃‍♀‍', title: 'Woman sprinting' },
        { character: '💃', title: 'Woman dancing' },
        { character: '🕺', title: 'Man dancing' },
        { character: '🕴', title: 'Person in suit levitating' },
        { character: '👯', title: 'People with bunny ears' },
        { character: '👯‍♂‍', title: 'Men with bunny ears' },
        { character: '👯‍♀‍', title: 'Women in bunny ears' },
        { character: '🧖', title: 'Person in steaming room' },
        { character: '🧖‍♂‍', title: 'Man in steaming room' },
        { character: '🧖‍♀‍', title: 'Woman in steaming room' },
        { character: '🧗', title: 'Person climbing' },
        { character: '🧗‍♂‍', title: 'Man climbing' },
        { character: '🧗‍♀‍', title: 'Woman climbing' },
        { character: '🤺', title: 'Person fencing' },
        { character: '🏇', title: 'Horse racing' },
        { character: '⛷', title: 'Skier' },
        { character: '🏂', title: 'Snowball' },
        { character: '🏌', title: 'Person playing golf' },
        { character: '🏌️‍♂‍', title: 'Man playing golf' },
        { character: '🏌️‍♀‍', title: 'Woman playing golf' },
        { character: '🏄', title: 'Person surfing' },
        { character: '🏄‍♂‍', title: 'Man surfing' },
        { character: '🏄‍♀‍', title: 'Woman surfing' },
        { character: '🚣', title: 'Person rowing boat' },
        { character: '🚣‍♂‍', title: 'Man rowing boat' },
        { character: '🚣‍♀‍', title: 'Woman rowing boat' },
        { character: '🏊', title: 'Person swimming' },
        { character: '🏊‍♂‍', title: 'Man swimming' },
        { character: '🏊‍♀‍', title: 'Woman swimming' },
        { character: '⛹', title: 'Person bouncing ball' },
        { character: '⛹️‍♂‍', title: 'Man bouncing ball' },
        { character: '⛹️‍♀‍', title: 'Woman bouncing ball' },
        { character: '🏋', title: 'Person lifting weight' },
        { character: '🏋️‍♂‍', title: 'Man lifting weight' },
        { character: '🏋️‍♀‍', title: 'Woman lifting weight' },
        { character: '🚴', title: 'Person cycling' },
        { character: '🚴‍♂‍', title: 'Man cycling' },
        { character: '🚴‍♀‍', title: 'Woman cycling' },
        { character: '🚵', title: 'Person mountain biking' },
        { character: '🚵‍♂‍', title: 'Man mountain biking' },
        { character: '🚵‍♀‍', title: 'Woman mountain biking' },
        { character: '🤸', title: 'Person catwheeling' },
        { character: '🤸‍♂‍', title: 'Man catwheeling' },
        { character: '🤸‍♀‍', title: 'Woman catwheeling' },
        { character: '🤼', title: 'People wrestling' },
        { character: '🤼‍♂‍', title: 'Men wrestling' },
        { character: '🤼‍♀‍', title: 'Women wrestling' },
        { character: '🤽', title: 'Person playing water polo' },
        { character: '🤽‍♂‍', title: 'Man playing water polo' },
        { character: '🤽‍♀‍', title: 'Woman playing water polo' },
        { character: '🤾', title: 'Person playing handball' },
        { character: '🤾‍♂‍', title: 'Man playing handball' },
        { character: '🤾‍♀‍', title: 'Woman playing handball' },
        { character: '🤹', title: 'Person juggling' },
        { character: '🤹‍♂‍', title: 'Man juggling' },
        { character: '🤹‍♀‍', title: 'Woman juggling' },
        { character: '🧘', title: 'Person lotus position' },
        { character: '🧘‍♂‍', title: 'Man in lotus position' },
        { character: '🧘‍♀‍', title: 'Woman in lotus position' },
        { character: '🛀', title: 'Person bathing' },
        { character: '🛌', title: 'Person in bed' },
        { character: '👪', title: 'Family' },
        { character: '👨‍👩‍👦', title: 'Family: man, woman, and boy' },
        { character: '👨‍👩‍👧', title: 'Family: man, woman, and girl' },
        { character: '👨‍👩‍👧‍👦', title: 'Family: man, woman, boy, and girl' },
        { character: '👨‍👩‍👦‍👦', title: 'Family: man, woman, and two boys' },
        { character: '👨‍👩‍👧‍👧', title: 'Family: man, woman, and two girls' },
        { character: '👨‍👨‍👦', title: 'Family: two men and boy' },
        { character: '👨‍👨‍👧', title: 'Family: two men and girl' },
        { character: '👨‍👨‍👧‍👦', title: 'Family: two men, girl, and boy' },
        { character: '👨‍👨‍👦‍👦', title: 'Family: two men and two boys' },
        { character: '👨‍👨‍👧‍👧', title: 'Family: two men and two girls' },
        { character: '👩‍👩‍👦', title: 'Family: two women and boy' },
        { character: '👩‍👩‍👧', title: 'Family: two women and girl' },
        { character: '👩‍👩‍👧‍👦', title: 'Family: two women, girl, and boy' },
        { character: '👩‍👩‍👦‍👦', title: 'Family: two women and two boys' },
        { character: '👩‍👩‍👧‍👧', title: 'Family: two women and two girls' },
        { character: '👨‍👦', title: 'Family: man and boy' },
        { character: '👨‍👦‍👦', title: 'Family: man and two boys' },
        { character: '👨‍👧', title: 'Family: man and girl' },
        { character: '👨‍👧‍👦', title: 'Family: man, girl and boy' },
        { character: '👨‍👧‍👧', title: 'Family: man and two girls' },
        { character: '👩‍👦', title: 'Family: woman and boy' },
        { character: '👩‍👦‍👦', title: 'Family: woman and two boys' },
        { character: '👩‍👧', title: 'Family: woman and girl' },
        { character: '👩‍👧‍👦', title: 'Family: woman, girl, and boy' },
        { character: '👩‍👧‍👧', title: 'Family: woman and two girls' },
        { character: '🧑‍🤝‍🧑', title: 'People holding hands' },
        { character: '👭', title: 'Women holding hands' },
        { character: '👫', title: 'Woman and man holding hands' },
        { character: '👬', title: 'Men holding hands' },
        { character: '💏', title: 'Kiss' },
        { character: '👩‍❤‍💋‍👨', title: 'Woman and man kissing' },
        { character: '👨‍❤‍💋‍👨', title: 'Man and man kissing' },
        { character: '👩‍❤‍💋‍👩', title: 'Womand and woman kissing' },
        { character: '💑', title: 'Couple with heart' },
        { character: '🗣', title: 'Person speaking' },
        { character: '👤', title: 'Bust in silhouhette' },
        { character: '👥', title: 'Busts in silhouette' },
        { character: '🫂', title: 'People hugging' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Animals and Nature Emojis', [
        { character: '🐵', title: 'Monkey face' },
        { character: '🐒', title: 'Monkey' },
        { character: '🦍', title: 'Gorilla' },
        { character: '🦧', title: 'Orangutan' },
        { character: '🐶', title: 'Dog face' },
        { character: '🐕', title: 'Dog' },
        { character: '🦮', title: 'Guide dog' },
        { character: '🐕‍🦺', title: 'Service dog' },
        { character: '🐩', title: 'Poodle' },
        { character: '🐺', title: 'Wolf' },
        { character: '🦊', title: 'Fox' },
        { character: '🦝', title: 'Racoon' },
        { character: '🐱', title: 'Cat face' },
        { character: '🐈', title: 'Cat' },
        { character: '🐈‍⬛', title: 'Black cat' },
        { character: '🦁', title: 'Lion' },
        { character: '🐯', title: 'Tiger face' },
        { character: '🐅', title: 'Tiger' },
        { character: '🐆', title: 'Leopard' },
        { character: '🐴', title: 'Horse face' },
        { character: '🐎', title: 'Horse' },
        { character: '🦄', title: 'Unicorn' },
        { character: '🦓', title: 'Zebra' },
        { character: '🦌', title: 'Deer' },
        { character: '🦬', title: 'Bison' },
        { character: '🐮', title: 'Cow face' },
        { character: '🐄', title: 'Cow' },
        { character: '🐂', title: 'Ox' },
        { character: '🐃', title: 'Water buffalo' },
        { character: '🐷', title: 'Pig face' },
        { character: '🐖', title: 'Pig' },
        { character: '🐗', title: 'Boar' },
        { character: '🐽', title: 'Pig nose' },
        { character: '🐏', title: 'Ram' },
        { character: '🐑', title: 'Ewe' },
        { character: '🐐', title: 'Goat' },
        { character: '🐪', title: 'Camel' },
        { character: '🐫', title: 'Two hump camel' },
        { character: '🦙', title: 'Ilama' },
        { character: '🦒', title: 'Giraffe' },
        { character: '🐘', title: 'Elephant' },
        { character: '🦣', title: 'Mammoth' },
        { character: '🦏', title: 'Rhiniceros' },
        { character: '🦛', title: 'Hippopotamus' },
        { character: '🐭', title: 'Mouse face' },
        { character: '🐁', title: 'Mouse' },
        { character: '🐀', title: 'Rat' },
        { character: '🐹', title: 'Hamster' },
        { character: '🐰', title: 'Rabbit face' },
        { character: '🐇', title: 'Rabbit' },
        { character: '🐿', title: 'Chipmunk' },
        { character: '🦫', title: 'Beaver' },
        { character: '🦔', title: 'Hedgehog' },
        { character: '🦇', title: 'Bat' },
        { character: '🐻', title: 'Bear' },
        { character: '🐻‍❄️', title: 'Polar bear' },
        { character: '🐨', title: 'Koala' },
        { character: '🐼', title: 'Panda' },
        { character: '🦥', title: 'Sloth' },
        { character: '🦦', title: 'Otter' },
        { character: '🦨', title: 'Skunk' },
        { character: '🦘', title: 'Kangaroo' },
        { character: '🦡', title: 'Badger' },
        { character: '🐾', title: 'Paw prints' },
        { character: '🦃', title: 'Turkey' },
        { character: '🐔', title: 'Chicken' },
        { character: '🐓', title: 'Rooster' },
        { character: '🐣', title: 'Hatching' },
        { character: '🐤', title: 'Baby chick' },
        { character: '🐥', title: 'Front-facing chick' },
        { character: '🐦', title: 'Bird' },
        { character: '🐧', title: 'Penguin' },
        { character: '🕊', title: 'Dove' },
        { character: '🦅', title: 'Eagle' },
        { character: '🦆', title: 'Duck' },
        { character: '🦢', title: 'Swan' },
        { character: '🦉', title: 'Owl' },
        { character: '🦤', title: 'Dodo' },
        { character: '🪶', title: 'Feather' },
        { character: '🦩', title: 'Flamingo' },
        { character: '🦜', title: 'Peacock' },
        { character: '🐸', title: 'Frog' },
        { character: '🐊', title: 'Crocodile' },
        { character: '🐢', title: 'Turtle' },
        { character: '🦎', title: 'Lizard' },
        { character: '🐍', title: 'Snake' },
        { character: '🐲', title: 'Dragon face' },
        { character: '🐉', title: 'Dragon' },
        { character: '🦕', title: 'Sauropod' },
        { character: '🦖', title: 'Tyranosaurus' },
        { character: '🐳', title: 'Spouting whale' },
        { character: '🐋', title: 'Whale' },
        { character: '🐬', title: 'Dolphin' },
        { character: '🦭', title: 'Seal' },
        { character: '🐟', title: 'Fish' },
        { character: '🐠', title: 'Tropical fish' },
        { character: '🐡', title: 'Blowfish' },
        { character: '🦈', title: 'Shark' },
        { character: '🐙', title: 'Octopus' },
        { character: '🐚', title: 'Spiral shell' },
        { character: '🐌', title: 'Snail' },
        { character: '🦋', title: 'Butterfly' },
        { character: '🐛', title: 'Bug' },
        { character: '🐜', title: 'Ant' },
        { character: '🐝', title: 'Honeybee' },
        { character: '🪲', title: 'Beetle' },
        { character: '🐞', title: 'Lady Beetle' },
        { character: '🦗', title: 'Cricket' },
        { character: '🪳', title: 'Cockroach' },
        { character: '🕷', title: 'Spider' },
        { character: '🕸', title: 'Spider web' },
        { character: '🦂', title: 'Scorpion' },
        { character: '🦟', title: 'Mosquito' },
        { character: '🪰', title: 'Fly' },
        { character: '🪱', title: 'Worm' },
        { character: '🦠', title: 'Microbe' },
        { character: '💐', title: 'Bouquet' },
        { character: '🌸', title: 'Cherry blossom' },
        { character: '💮', title: 'White flower' },
        { character: '🏵', title: 'Rosette' },
        { character: '🌹', title: 'Rose' },
        { character: '🥀', title: 'Wilted flower' },
        { character: '🌺', title: 'Hibiscus' },
        { character: '🌻', title: 'Sunflower' },
        { character: '🌼', title: 'Blossom' },
        { character: '🌷', title: 'Tulip' },
        { character: '🌱', title: 'Seedling' },
        { character: '🪴', title: 'Potted plant' },
        { character: '🌲', title: 'Evergreen tree' },
        { character: '🌳', title: 'Deciduous plant' },
        { character: '🌴', title: 'Palm tree' },
        { character: '🌵', title: 'Cactus' },
        { character: '🌾', title: 'Sheaf of rice' },
        { character: '🌿', title: 'Herb' },
        { character: '☘', title: 'Shamrock' },
        { character: '🍀', title: 'Four leaf clover' },
        { character: '🍁', title: 'Maple leaf' },
        { character: '🍂', title: 'Fallen leaf' },
        { character: '🍃', title: 'Leaf fluttering in wind' },
        { character: '🪴', title: 'Empty nest' },
        { character: '🪴', title: 'Nest with eggs' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Food and Drinks Emojis', [
        { character: '🍇', title: 'Grapes' },
        { character: '🍈', title: 'Melon' },
        { character: '🍉', title: 'Water melon' },
        { character: '🍊', title: 'Tangerine' },
        { character: '🍋', title: 'Lime' },
        { character: '🍌', title: 'Banana' },
        { character: '🍍', title: 'Pineapple' },
        { character: '🥭', title: 'Mango' },
        { character: '🍎', title: 'Red apple' },
        { character: '🍏', title: 'Green apple' },
        { character: '🍐', title: 'Pear' },
        { character: '🍑', title: 'Peach' },
        { character: '🍒', title: 'Cherries' },
        { character: '🍓', title: 'Strawberries' },
        { character: '🫐', title: 'Blueberries' },
        { character: '🥝', title: 'Kiwi fruit' },
        { character: '🍅', title: 'Tomato' },
        { character: '🫒', title: 'Olive' },
        { character: '🥥', title: 'Coconut' },
        { character: '🥑', title: 'Avocado' },
        { character: '🍆', title: 'Eggplant' },
        { character: '🥔', title: 'Potato' },
        { character: '🥕', title: 'Carrot' },
        { character: '🌽', title: 'Corn' },
        { character: '🌶', title: 'Pepper' },
        { character: '🫑', title: 'Bell pepper' },
        { character: '🥒', title: 'Cucumber' },
        { character: '🥬', title: 'Leafy green' },
        { character: '🥦', title: 'Broccoli' },
        { character: '🧄', title: 'Garlic' },
        { character: '🧅', title: 'Onion' },
        { character: '🍄', title: 'Mushroom' },
        { character: '🥜', title: 'Peanuts' },
        { character: '🫑', title: 'Beans' },
        { character: '🌰', title: 'Chestnut' },
        { character: '🍞', title: 'Bread' },
        { character: '🥐', title: 'Croissant' },
        { character: '🥖', title: 'Baguette bread' },
        { character: '🫓', title: 'Flat bread' },
        { character: '🥨', title: 'Pretzel' },
        { character: '🥯', title: 'Bagel' },
        { character: '🥞', title: 'Pancake' },
        { character: '🧇', title: 'Waffle' },
        { character: '🧀', title: 'Cheese wedge' },
        { character: '🍖', title: 'Meat with bone' },
        { character: '🍗', title: 'Poultry leg' },
        { character: '🥩', title: 'Cut of meat' },
        { character: '🥓', title: 'Bacon' },
        { character: '🍔', title: 'Hamburger' },
        { character: '🍟', title: 'French fries' },
        { character: '🍕', title: 'Pizza' },
        { character: '🌭', title: 'Hot dog' },
        { character: '🥪', title: 'Sandwich' },
        { character: '🌮', title: 'Taco' },
        { character: '🌯', title: 'Burrito' },
        { character: '🫔', title: 'Tamale' },
        { character: '🥙', title: 'Stuffed flatbread' },
        { character: '🧆', title: 'Falafel' },
        { character: '🥚', title: 'Egg' },
        { character: '🍳', title: 'Cooking' },
        { character: '🥘', title: 'Shallow pan of food' },
        { character: '🍲', title: 'Pot of food' },
        { character: '🫕', title: 'Fondue' },
        { character: '🥣', title: 'Bowl with food' },
        { character: '🥗', title: 'Green salad' },
        { character: '🍿', title: 'Popcorn' },
        { character: '🧈', title: 'Butter' },
        { character: '🧂', title: 'Salt' },
        { character: '🥫', title: 'Canned food' },
        { character: '🍱', title: 'Bento box' },
        { character: '🍘', title: 'RIce cracker' },
        { character: '🍙', title: 'Rice ball' },
        { character: '🍚', title: 'Cooked rice' },
        { character: '🍛', title: 'Curry rice' },
        { character: '🍜', title: 'Steaming bowl' },
        { character: '🍝', title: 'Spaghetti' },
        { character: '🍠', title: 'Roasted sweet potato' },
        { character: '🍢', title: 'Oden' },
        { character: '🍣', title: 'Sushi' },
        { character: '🍤', title: 'Fried shrimp' },
        { character: '🍥', title: 'Fish cake with swiri' },
        { character: '🥮', title: 'Moon cake' },
        { character: '🍡', title: 'Dango' },
        { character: '🥟', title: 'Dumpling' },
        { character: '🥠', title: 'Fortune cookie' },
        { character: '🥡', title: 'Take out box' },
        { character: '🦀', title: 'Crab' },
        { character: '🦞', title: 'Lobster' },
        { character: '🦐', title: 'Shrimp' },
        { character: '🦑', title: 'Squid' },
        { character: '🦪', title: 'Oyster' },
        { character: '🍨', title: 'Ice cream' },
        { character: '🍧', title: 'Shaved ice cream' },
        { character: '🍦', title: 'Soft ice cream' },
        { character: '🍩', title: 'Doughnut' },
        { character: '🍪', title: 'Cookie' },
        { character: '🎂', title: 'Birthday cake' },
        { character: '🍰', title: 'Short cake' },
        { character: '🧁', title: 'Cup cake' },
        { character: '🥧', title: 'Pie' },
        { character: '🍫', title: 'Chocoloate' },
        { character: '🍬', title: 'Candy' },
        { character: '🍭', title: 'Lollipop' },
        { character: '🍮', title: 'Custard' },
        { character: '🍯', title: 'Honey pot' },
        { character: '🍼', title: 'Baby bottle' },
        { character: '🥛', title: 'Glass of milk' },
        { character: '☕', title: 'Hot beverage' },
        { character: '🫖', title: 'Teapot' },
        { character: '🍵', title: 'Teacup without handle' },
        { character: '🍶', title: 'Sake' },
        { character: '🍾', title: 'Bottle with poppin cork' },
        { character: '🍷', title: 'Wine glass' },
        { character: '🍸', title: 'Cocktail glass' },
        { character: '🍹', title: 'Tropical drink' },
        { character: '🍺', title: 'Beer mug' },
        { character: '🍻', title: 'Clinking beer mug' },
        { character: '🥂', title: 'Clinking glasses' },
        { character: '🥃', title: 'Tumbler glass' },
        { character: '🥤', title: 'Cup with strawberry' },
        { character: '🧋', title: 'Bubble tea' },
        { character: '🧃', title: 'Beverage box' },
        { character: '🧉', title: 'Mate' },
        { character: '🧊', title: 'Ice' },
        { character: '🥢', title: 'Chopsticks' },
        { character: '🍽', title: 'Fork and knife with plate' },
        { character: '🍴', title: 'Fork and knife' },
        { character: '🥄', title: 'Spoon' },
        { character: '🔪', title: 'Kitchen knife' },
        { character: '🧋', title: 'Jar' },
        { character: '🏺', title: 'Amphora' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Travel and Places Emojis', [
        { character: '🌍', title: 'Globe showing Africa and Europe' },
        { character: '🌎', title: 'Globe showing Americas' },
        { character: '🌏', title: 'Globe showing Asia and Australia' },
        { character: '🌐', title: 'Globe with meridians' },
        { character: '🗺', title: 'World map' },
        { character: '🧭', title: 'Compass' },
        { character: '⛰', title: 'Mountain' },
        { character: '🏔', title: 'Snowcap mountain' },
        { character: '🌋', title: 'Volcanic mountain' },
        { character: '🗻', title: 'Fuji mountain' },
        { character: '🏕', title: 'Camping' },
        { character: '🏖', title: 'Beach with umbrella' },
        { character: '🏜', title: 'Desert' },
        { character: '🏝', title: 'Desertified island' },
        { character: '🏞', title: 'National park' },
        { character: '🏟', title: 'Stadium' },
        { character: '🏛', title: 'Classical building' },
        { character: '🏗', title: 'Building construction' },
        { character: '🧱', title: 'Brick' },
        { character: '🪨', title: 'Rock' },
        { character: '🪵', title: 'Wood' },
        { character: '🛖', title: 'Hut' },
        { character: '🏘', title: 'Houses' },
        { character: '🏚', title: 'Derelict house' },
        { character: '🏠', title: 'House' },
        { character: '🏡', title: 'House with garden' },
        { character: '🏢', title: 'Office building' },
        { character: '🏣', title: 'Japanese office' },
        { character: '🏤', title: 'Post office' },
        { character: '🏥', title: 'Hospital' },
        { character: '🏦', title: 'Bank' },
        { character: '🏨', title: 'Hotel' },
        { character: '🏩', title: 'Love hotel' },
        { character: '🏪', title: 'Convenience store' },
        { character: '🏫', title: 'School' },
        { character: '🏬', title: 'Department' },
        { character: '🏭', title: 'Factory' },
        { character: '🏯', title: 'Japanese castle' },
        { character: '🏰', title: 'Castle' },
        { character: '💒', title: 'Wedding house' },
        { character: '🗼', title: 'Tokyo tower' },
        { character: '🗽', title: 'Statue of liberty' },
        { character: '⛪', title: 'Church' },
        { character: '🕌', title: 'Mosque' },
        { character: '🛕', title: 'Hindu temple' },
        { character: '🕍', title: 'Synagogue' },
        { character: '⛩', title: 'Shinto shrine' },
        { character: '🕋', title: 'Kaaba' },
        { character: '⛲', title: 'Fountain' },
        { character: '⛺', title: 'Tent' },
        { character: '🌁', title: 'Foggy' },
        { character: '🌃', title: 'Night with starrs' },
        { character: '🏙', title: 'Citscape' },
        { character: '🌅', title: 'Sunrise' },
        { character: '🌄', title: 'Sunrise over mountains' },
        { character: '🌆', title: 'Cityscape at dusk' },
        { character: '🌇', title: 'Sunset' },
        { character: '🌉', title: 'Bridge at night' },
        { character: '♨', title: 'Hot springs' },
        { character: '🎠', title: 'Carousel horse' },
        { character: '🎡', title: 'Ferris wheel' },
        { character: '🎢', title: 'Roller coaster' },
        { character: '💈', title: 'Barber poll' },
        { character: '🎪', title: 'Circus tent' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Transport Emojis', [
        { character: '🚂', title: 'Locomotive' },
        { character: '🚃', title: 'Railway car' },
        { character: '🚄', title: 'High speed train' },
        { character: '🚅', title: 'Bullet train' },
        { character: '🚆', title: 'Train' },
        { character: '🚇', title: 'Metro' },
        { character: '🚈', title: 'Light rail' },
        { character: '🚉', title: 'Station' },
        { character: '🚊', title: 'Tram' },
        { character: '🚝', title: 'Monorail' },
        { character: '🚞', title: 'Mountain railway' },
        { character: '🚋', title: 'Tram car' },
        { character: '🚌', title: 'us' },
        { character: '🚍', title: 'Oncoming bus' },
        { character: '🚎', title: 'Trolley bus' },
        { character: '🚐', title: 'Minibus' },
        { character: '🚑', title: 'Ambulance' },
        { character: '🚒', title: 'Fire engine' },
        { character: '🚓', title: 'Police car' },
        { character: '🚔', title: 'Oncoming police car' },
        { character: '🚕', title: 'Taxi' },
        { character: '🚖', title: 'Oncoming taxi' },
        { character: '🚗', title: 'Automobile' },
        { character: '🚘', title: 'Oncoming automobile' },
        { character: '🚙', title: 'Sport utility vehicle' },
        { character: '🛻', title: 'Pickup truck' },
        { character: '🚚', title: 'Delivery truck' },
        { character: '🚛', title: 'Articulated lorry' },
        { character: '🚜', title: 'Tractor' },
        { character: '🏎', title: 'Racing car' },
        { character: '🏍', title: 'Motorcycle' },
        { character: '🛵', title: 'Scooter' },
        { character: '🦽', title: 'Manual wheelchair' },
        { character: '🦼', title: 'Motorized wheelchair' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Time Emojis', [
        { character: '⌛', title: 'Hourglass done' },
        { character: '⏳', title: 'Hourglass starting' },
        { character: '⌚', title: 'Watch' },
        { character: '⏰', title: 'Alarm' },
        { character: '⏱', title: 'Stopwatch' },
        { character: '⏲', title: 'Timer clock' },
        { character: '🕰', title: 'Mantelpiece clock' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Sky and Weather Emojis', [
        { character: '🌛', title: 'First quarter moon face' },
        { character: '🌜', title: 'Last quartermoon face' },
        { character: '🌡', title: 'Thermometer' },
        { character: '☀', title: 'Sun' },
        { character: '🌝', title: 'Full moon face' },
        { character: '🌞', title: 'Sun with face' },
        { character: '🪐', title: 'Ringed planet' },
        { character: '⭐', title: 'Star' },
        { character: '🌟', title: 'Glowing star' },
        { character: '🌠', title: 'Shooting star' },
        { character: '🌌', title: 'Milky way' },
        { character: '☁', title: 'Cloud' },
        { character: '⛅', title: 'Sun behind cloud' },
        { character: '⛈', title: 'Cloud with lighting and rain' },
        { character: '🌤', title: 'Sun behind small cloud' },
        { character: '🌥', title: 'Sun behind large cloud' },
        { character: '🌦', title: 'Sun behind rain cloud' },
        { character: '🌧', title: 'Cloud with rain' },
        { character: '🌨', title: 'Cloud with snow' },
        { character: '🌩', title: 'Cloud with lighting' },
        { character: '🌪', title: 'Tornado' },
        { character: '🌫', title: 'Fog' },
        { character: '🌬', title: 'Wind face' },
        { character: '🌀', title: 'Cyclone' },
        { character: '🌈', title: 'Rainbow' },
        { character: '🌂', title: 'Closed umbrella' },
        { character: '☂', title: 'Umbrella' },
        { character: '☔', title: 'Umbrella with raindrops' },
        { character: '⛱', title: 'Umbrella on ground' },
        { character: '⚡', title: 'High voltage' },
        { character: '❄', title: 'Snowflake' },
        { character: '☃', title: 'Snowman' },
        { character: '⛄', title: 'Snowman without snow' },
        { character: '☄', title: 'Comet' },
        { character: '🔥', title: 'Fire' },
        { character: '💧', title: 'Droplet' },
        { character: '🌊', title: 'Water wave' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Activity Emojis', [
        { character: '🎃', title: 'Jack-o-lantern' },
        { character: '🎄', title: 'Christmas tree' },
        { character: '🎆', title: 'Fireworks' },
        { character: '🎇', title: 'Sparkler' },
        { character: '🧨', title: 'Firecracker' },
        { character: '✨', title: 'Sparkles' },
        { character: '🎈', title: 'Baloon' },
        { character: '🎉', title: 'Party popper' },
        { character: '🎊', title: 'Confetti ball' },
        { character: '🎋', title: 'Tanabata tree' },
        { character: '🎍', title: 'Pine decoration' },
        { character: '🎎', title: 'Japanese dolls' },
        { character: '🎏', title: 'Carp streamer' },
        { character: '🎑', title: 'Moon viewing ceremony' },
        { character: '🧧', title: 'Red envelope' },
        { character: '🎀', title: 'Ribbon' },
        { character: '🎁', title: 'Wrapped gift' },
        { character: '🎗', title: 'Reminder ribbon' },
        { character: '🎟', title: 'Admission ticket' },
        { character: '🎫', title: 'Ticket' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Award Medals Emojis', [
        { character: '🎖', title: 'Military medal' },
        { character: '🏆', title: 'Trophy' },
        { character: '🏅', title: 'Sports medal' },
        { character: '🥇', title: 'Gold medal - first position' },
        { character: '🥈', title: 'Silver medal - second position' },
        { character: '🥉', title: 'Bronze medal - third position' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Sport Emojis', [
        { character: '⚽', title: 'Soccer ball' },
        { character: '⚾', title: 'Baseball' },
        { character: '🥎', title: 'Softball' },
        { character: '🏀', title: 'BAsketball' },
        { character: '🏐', title: 'Volleyball' },
        { character: '🏈', title: 'American football' },
        { character: '🏉', title: 'Rugby' },
        { character: '🎾', title: 'Tennis' },
        { character: '🥏', title: 'Flying disk' },
        { character: '🎳', title: 'Bowling' },
        { character: '🏏', title: 'Cricket' },
        { character: '🏑', title: 'Field hockey' },
        { character: '🏒', title: 'Ice hockey' },
        { character: '🥍', title: 'Lacrose' },
        { character: '🏓', title: 'Ping pong' },
        { character: '🏸', title: 'Badminton' },
        { character: '🥊', title: 'Boxing glove' },
        { character: '🥋', title: 'Martial arts uniform' },
        { character: '🥅', title: 'Goal net' },
        { character: '⛳', title: 'Flag in a hole' },
        { character: '⛸', title: 'Ice skate' },
        { character: '🎣', title: 'Fishing poll' },
        { character: '🤿', title: 'Driving mask' },
        { character: '🎽', title: 'Running shirt' },
        { character: '🎿', title: 'Skis' },
        { character: '🛷', title: 'Sled' },
        { character: '🥌', title: 'Curling stone' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Games Emojis', [
        { character: '🎯', title: 'Bullseye' },
        { character: '🪀', title: 'Yo-yo' },
        { character: '🪁', title: 'Kite' },
        { character: '🎱', title: '8 ball' },
        { character: '🔮', title: 'Crystal ball' },
        { character: '🪄', title: 'Magic wand' },
        { character: '🧿', title: 'Nazar amulet' },
        { character: '🪄', title: 'Hamsa' },
        { character: '🎮', title: 'Video game pad' },
        { character: '🕹', title: 'Joystick' },
        { character: '🎰', title: 'Slot machine' },
        { character: '🎲', title: 'Game die' },
        { character: '🧩', title: 'Puxxle piece' },
        { character: '🧸', title: 'Teddy bear' },
        { character: '🪅', title: 'Pinata' },
        { character: '🪆', title: 'Mirror' },
        { character: '🪆', title: 'Nesting doll' },
        { character: '♠', title: 'Spade suit' },
        { character: '♥', title: 'Heart suit' },
        { character: '♣', title: 'Club suit' },
        { character: '♟', title: 'Chess pawn' },
        { character: '🃏', title: 'Joker' },
        { character: '🀄', title: 'Mahjong red dragon' },
        { character: '🎴', title: 'Flower playing cards' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Arts and Crafts Emojis', [
        { character: '🎭', title: 'Performing arts' },
        { character: '🖼', title: 'Framed picture' },
        { character: '🎨', title: 'Artist pallete' },
        { character: '🧵', title: 'Thread' },
        { character: '🪡', title: 'Sewing needle with thred' },
        { character: '🧶', title: 'Yarn' },
        { character: '🪢', title: 'Knot' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Clothing Objects Emojis', [
        { character: '👓', title: 'Glasses' },
        { character: '🕶', title: 'Sunglasses' },
        { character: '🥽', title: 'Googles' },
        { character: '🥼', title: 'Lab coat' },
        { character: '🦺', title: 'Safety vest' },
        { character: '👔', title: 'Necktie' },
        { character: '👕', title: 'T-shirt' },
        { character: '👖', title: 'Jeans' },
        { character: '🧣', title: 'Scarf' },
        { character: '🧤', title: 'Gloves' },
        { character: '🧥', title: 'Coat' },
        { character: '🧦', title: 'Socks' },
        { character: '👗', title: 'Dress' },
        { character: '👘', title: 'Kimono' },
        { character: '🥻', title: 'Sari' },
        { character: '🩱', title: 'One piece suit' },
        { character: '🩲', title: 'Briefs' },
        { character: '🩳', title: 'Shorts' },
        { character: '👙', title: 'Bikini' },
        { character: '👚', title: 'Woman\'s cloth' },
        { character: '👛', title: 'Purse' },
        { character: '👜', title: 'Handbag' },
        { character: '👝', title: 'Clutch bag' },
        { character: '🛍', title: 'Shopping bags' },
        { character: '🎒', title: 'Backpack' },
        { character: '🩴', title: 'Thong sandals' },
        { character: '👞', title: 'Man\'s shoe' },
        { character: '👟', title: 'Running shoe' },
        { character: '🥾', title: 'Hiking boot' },
        { character: '🥿', title: 'Flat shoe' },
        { character: '👠', title: 'High-heeled shoe' },
        { character: '👡', title: 'Woman\'s sandal' },
        { character: '🩰', title: 'Ballet shoes' },
        { character: '👢', title: 'Woman\'s boot' },
        { character: '👑', title: 'Crown' },
        { character: '👒', title: 'Woman\'s hat' },
        { character: '🎩', title: 'Top hat' },
        { character: '🎓', title: 'Graduation cap' },
        { character: '🧢', title: 'Billed cap' },
        { character: '🪖', title: 'Military helmet' },
        { character: '⛑', title: 'Rescuew worker\'s helmet' },
        { character: '📿', title: 'PRayer beads' },
        { character: '💄', title: 'Lipstick' },
        { character: '💍', title: 'Ring' },
        { character: '💎', title: 'Gemstone' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Sound Emojis', [
        { character: '🔈', title: 'Low volume speaker' },
        { character: '📢', title: 'Loudspeaker' },
        { character: '📣', title: 'Megaphone' },
        { character: '📯', title: 'Postal horn' },
        { character: '🔔', title: 'Bell' },
        { character: '🎼', title: 'Musical score' },
        { character: '🎵', title: 'Musical note' },
        { character: '🎶', title: 'Musical notes' },
        { character: '🎙', title: 'Studio microphone' },
        { character: '🎚', title: 'Level slider' },
        { character: '🎛', title: 'Control knobs' },
        { character: '🎤', title: 'Microphone' },
        { character: '🎧', title: 'Headphone' },
        { character: '📻', title: 'Radio' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Musical Instrument Emojis', [
        { character: '🎷', title: 'Saxophone' },
        { character: '🪗', title: 'Accordion' },
        { character: '🎸', title: 'Guitar' },
        { character: '🎹', title: 'Musical keyboard' },
        { character: '🎺', title: 'Trumpet' },
        { character: '🎻', title: 'Violin' },
        { character: '🪕', title: 'Banjo' },
        { character: '🥁', title: 'Drum' },
        { character: '🪘', title: 'Long drum' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Technology Emojis', [
        { character: '📱', title: 'Mobile phone' },
        { character: '📞', title: 'Telephone receiver' },
        { character: '📟', title: 'Pager' },
        { character: '📠', title: 'Fax machine' },
        { character: '🔋', title: 'Full battery' },
        { character: '🔌', title: 'Electric plug' },
        { character: '💻', title: 'Laptop' },
        { character: '🖥', title: 'Desktop computer' },
        { character: '🖨', title: 'Printer' },
        { character: '⌨', title: 'Keyboard' },
        { character: '🖱', title: 'Mouse' },
        { character: '🎥', title: 'Movie camera' },
        { character: '🎞', title: 'Film frames' },
        { character: '📽', title: 'Film Projector' },
        { character: '🎬', title: 'Clapper board' },
        { character: '📺', title: 'Television' },
        { character: '📷', title: 'Camera' },
        { character: '📸', title: 'Camera with flash' },
        { character: '📹', title: 'Video camera' },
        { character: '📼', title: 'Video cassete' },
        { character: '🔍', title: 'Magnifying glass tilted left' },
        { character: '🔎', title: 'Magnifying glass tilted right' },
        { character: '🕯', title: 'Candle' },
        { character: '💡', title: 'Light bulb' },
        { character: '🔦', title: 'Flashlight' },
        { character: '🏮', title: 'Red pepper lantern' },
        { character: '🪔', title: 'Diya lamp' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Book and Paper Emojis', [
        { character: '📔', title: 'Notebook with decorative cover' },
        { character: '📕', title: 'Closed notebook' },
        { character: '📖', title: 'Opened notebook' },
        { character: '📗', title: 'Green book' },
        { character: '📘', title: 'Blue book' },
        { character: '📙', title: 'Orange book' },
        { character: '📚', title: 'Orange books' },
        { character: '📓', title: 'Notebook' },
        { character: '📒', title: 'Ledger' },
        { character: '📃', title: 'Page with curl' },
        { character: '📜', title: 'Scroll' },
        { character: '📄', title: 'Page facing up' },
        { character: '📰', title: 'Newspaper' },
        { character: '🗞', title: 'Rolled-up newspaper' },
        { character: '📑', title: 'Bookmark tabs' },
        { character: '🔖', title: 'Bookmark' },
        { character: '🏷', title: 'Label' },
        { character: '💰', title: 'Money bag' },
        { character: '🪙', title: 'Coin' },
        { character: '💴', title: 'Yen banknote' },
        { character: '💵', title: 'Dollar banknote' },
        { character: '💶', title: 'Euro banknote' },
        { character: '💷', title: 'Pound banknote' },
        { character: '💸', title: 'Money with wings' },
        { character: '💳', title: 'Credit card' },
        { character: '🧾', title: 'Receipt' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Office Emojis', [
        { character: '✏', title: 'Pencil' },
        { character: '✒', title: 'Black nib' },
        { character: '🖋', title: 'Fountain pen' },
        { character: '🖊', title: 'Pen' },
        { character: '🖌', title: 'Paintbrush' },
        { character: '🖍', title: 'Crayon' },
        { character: '📝', title: 'Memo' },
        { character: '💼', title: 'Briefcase' },
        { character: '📁', title: 'File folder' },
        { character: '🗂', title: 'Card index dividers' },
        { character: '📅', title: 'Calender' },
        { character: '📆', title: 'Tear off calender' },
        { character: '📇', title: 'Card index' },
        { character: '📌', title: 'Pushpin' },
        { character: '📎', title: 'Paperclip' },
        { character: '📏', title: 'Straight ruler' },
        { character: '📐', title: 'Triangular ruler' },
        { character: '✂', title: 'Scissors' },
        { character: '🗃', title: 'Card file box' },
        { character: '🗄', title: 'File cabinet' },
        { character: '🗑', title: 'Waste basket' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Tools Emojis', [
        { character: '🔨', title: 'Hammer' },
        { character: '🪓', title: 'Axe' },
        { character: '⛏', title: 'Pick' },
        { character: '⚒', title: 'Hammer and pick' },
        { character: '🛠', title: 'Hammer and wrench' },
        { character: '🗡', title: 'Sword' },
        { character: '⚔', title: 'Crossed swords' },
        { character: '🔫', title: 'Water gun' },
        { character: '🪃', title: 'Boomerang' },
        { character: '🏹', title: 'Bow and arrow' },
        { character: '🛡', title: 'Shield' },
        { character: '🪚', title: 'Carpentry saw' },
        { character: '🔧', title: 'Wrench' },
        { character: '🪛', title: 'Screwdriver' },
        { character: '🔩', title: 'Bolt and nut' },
        { character: '⚙', title: 'Wheel' },
        { character: '🗜', title: 'Clamp' },
        { character: '⚖', title: 'Balance scale' },
        { character: '🦯', title: 'White cane' },
        { character: '🔗', title: 'Link' },
        { character: '⛓', title: 'Chains' },
        { character: '🪝', title: 'Hook' },
        { character: '🧰', title: 'Toolbox' },
        { character: '🧲', title: 'Magnet' },
        { character: '🪜', title: 'Ladder' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Household Emojis', [
        { character: '🚪', title: 'Door' },
        { character: '🛗', title: 'Elevator' },
        { character: '🪞', title: 'Mirror' },
        { character: '🪟', title: 'Window' },
        { character: '🛏', title: 'Bed' },
        { character: '🛋', title: 'Couch and lamp' },
        { character: '🪑', title: 'Chair' },
        { character: '🚽', title: 'Toilet' },
        { character: '🪠', title: 'Plunger' },
        { character: '🚿', title: 'Shower' },
        { character: '🛁', title: 'Bathtub' },
        { character: '🪤', title: 'Mouse trap' },
        { character: '🪒', title: 'Razor' },
        { character: '🧴', title: 'Lotion bottle' },
        { character: '🧷', title: 'Safety pin' },
        { character: '🧹', title: 'Broom' },
        { character: '🧺', title: 'Basket' },
        { character: '🧻', title: 'Roll of paper' },
        { character: '🪣', title: 'Bucket' },
        { character: '🧼', title: 'Soap' },
        { character: '🫧', title: 'Bubbles' },
        { character: '🪥', title: 'Toothbrush' },
        { character: '🧽', title: 'Sponge' },
        { character: '🧯', title: 'Fire extinguisher' },
        { character: '🛒', title: 'Shopping cart' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Warning Emojis', [
        { character: '⚠', title: 'Warning' },
        { character: '🚸', title: 'Children crossing' },
        { character: '⛔', title: 'No entry' },
        { character: '🚫', title: 'Prohibited' },
        { character: '🚳', title: 'No bicycles' },
        { character: '🚭', title: 'No smoking' },
        { character: '🚯', title: 'No littering' },
        { character: '🚱', title: 'Non-portable water' },
        { character: '🚷', title: 'No pedestrians' },
        { character: '📵', title: 'No mobile phones' },
        { character: '🔞', title: 'No one under 18' },
        { character: '☢', title: 'Radioactive' },
        { character: '☣', title: 'Biohazard' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Arrow Emojis', [
        { character: '⬆', title: 'Up Arrow' },
        { character: '↗', title: 'Up-right arrow' },
        { character: '➡', title: 'Right arrow' },
        { character: '↘', title: 'Down-right arrow' },
        { character: '⬇', title: 'Down arrow' },
        { character: '↙', title: 'Down-left arrow' },
        { character: '⬅', title: 'Left arrow' },
        { character: '↖', title: 'Up-left arrow' },
        { character: '↕', title: 'Up-down arrow' },
        { character: '↔', title: 'Left arrow' },
        { character: '↩', title: 'Right arrow curving left' },
        { character: '↪', title: 'Left arrow curving right' },
        { character: '⤴', title: 'Right arrow curving up' },
        { character: '⤵', title: 'Right arrow curving down' },
        { character: '🔃', title: 'Clockwise vertical arrow' },
        { character: '🔄', title: 'Counterclockwise arrows button' },
        { character: '🔙', title: 'Back arrow' },
        { character: '🔚', title: 'End arrow' },
        { character: '🔛', title: 'On arrow' },
        { character: '🔜', title: 'Soon arrow' },
        { character: '🔝', title: 'Top arrow' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Symbols', [
        { character: '✖', title: 'Times' },
        { character: '➕', title: 'Plus' },
        { character: '➖', title: 'Minus' },
        { character: '➗', title: 'Divide' },
        { character: '🟰', title: 'Equals' },
        { character: '♾', title: 'Infinity' },
        { character: '‼', title: 'Double exclamation' },
        { character: '⁉', title: 'Exclamation and question mark' },
        { character: '❓', title: 'Red question mark' },
        { character: '❔', title: 'White question mark' },
        { character: '❗', title: 'Red exclamation mark' },
        { character: '❕', title: 'White exclamation mark' },
        { character: '〰', title: 'Wavy dash' },
        { character: '💱', title: 'Currency exchange' },
        { character: '💲', title: 'Heavy green dollar sign' },
        { character: '⚕', title: 'Medical symbol' },
        { character: '♻', title: 'Recycling symbol' },
        { character: '⚜', title: 'Fleur-de-lis' },
        { character: '🔱', title: 'Trident' },
        { character: '📛', title: 'Name badge' },
        { character: '🔰', title: 'Japanese symbol for beginner' },
        { character: '⭕', title: 'Hollow red circle' },
        { character: '✅', title: 'Green box with checkmark' },
        { character: '☑', title: 'Blue box with checkmark' },
        { character: '✔', title: 'Checkmark' },
        { character: '❌', title: 'Crossmark' },
        { character: '❎', title: 'Green crossmark' },
        { character: '➰', title: 'Curly loop' },
        { character: '➿', title: 'Double curly loop' },
        { character: '〽', title: 'PArt alternation mark' },
        { character: '✳', title: 'Eight-spoked asterik' },
        { character: '✴', title: 'Eight-pointed star' },
        { character: '❇', title: 'Sparkle' },
        { character: '©', title: 'Copyright symbol' },
        { character: '®', title: 'Registered' },
        { character: '™', title: 'Trademark' },
        { character: '#', title: '># Keycap' },
        { character: '*', title: '>* Keycap' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Geometry', [
        { character: '🔴', title: 'Red circle' },
        { character: '🟠', title: 'Orange circle' },
        { character: '🟡', title: 'Yellow circle' },
        { character: '🟢', title: 'Green circle' },
        { character: '🔵', title: 'Blue circle' },
        { character: '🟣', title: 'Purple circle' },
        { character: '🟤', title: 'Brown circle' },
        { character: '⚫', title: 'Black circle' },
        { character: '⚪', title: 'White circle' },
        { character: '🟥', title: 'Red square' },
        { character: '🟧', title: 'Orange square' },
        { character: '🟨', title: 'Yellow square' },
        { character: '🟩', title: 'Green square' },
        { character: '🟦', title: 'Blue square' },
        { character: '🟪', title: 'Purple square' },
        { character: '🟫', title: 'Brown square' },
        { character: '⬛', title: 'Black square' },
        { character: '⬜', title: 'White square' },
        { character: '🔶', title: 'Large orange diamond' },
        { character: '🔷', title: 'Large blue diamond' },
        { character: '🔸', title: 'Small orange diamond' },
        { character: '🔹', title: 'Small blue diamond' },
        { character: '🔺', title: 'Red triangle pointed up' },
        { character: '🔻', title: 'Red triangle pointed down' },
        { character: '💠', title: 'Diamond with a dot' },
        { character: '🔘', title: 'Radio button' },
        { character: '🔳', title: 'White square button' },
        { character: '🔲', title: 'Black square button' }
    ]);

    editor.plugins.get( 'SpecialCharacters' ).addItems( 'Flags', [
        { character: '🏁', title: 'Chequered flag' },
        { character: '🚩', title: 'Triangular flag' },
        { character: '🎌', title: 'Crossed flag' },
        { character: '🏴', title: 'Black flag' },
        { character: '🏳', title: 'White flag' },
        { character: '🏳️‍🌈', title: 'Rainbow flag' },
        { character: '🏳️‍⚧️', title: 'Transgender flag' },
        { character: '🏴‍☠️', title: 'Pirate flag' },
        { character: '🇦🇨', title: 'Ascension Island flag' },
        { character: '🇦🇩', title: 'Andorra flag' },
        { character: '🇦🇪', title: 'UAE flag' },
        { character: '🇦🇫', title: 'Afghanistan flag' },
        { character: '🇦🇬', title: 'Antigua and Berbuda flag' },
        { character: '🇦🇮', title: 'Anguila flag' },
        { character: '🇦🇱', title: 'Albania flag' },
        { character: '🇦🇲', title: 'Armenia flag' },
        { character: '🇦🇴', title: 'Angola flag' },
        { character: '🇦🇶', title: 'Antarctica flag' },
        { character: '🇦🇷', title: 'Argentina flag' },
        { character: '🇦🇸', title: 'American Samoa flag' },
        { character: '🇦🇹', title: 'Austria flag' },
        { character: '🇦🇺', title: 'Australia flag' },
        { character: '🇦🇼', title: 'Aruba flag' },
        { character: '🇦🇽', title: 'Åland Islands flag' },
        { character: '🇦🇿', title: 'Azerbaijan flag' },
        { character: '🇧🇦', title: 'Bosnia flag' },
        { character: '🇧🇧', title: 'Barbados flag' },
        { character: '🇧🇩', title: 'Bangladesh flag' },
        { character: '🇧🇪', title: 'Belgium flag' },
        { character: '🇧🇫', title: 'Burkina Faso flag' },
        { character: '🇧🇬', title: 'Bulgaria flag' },
        { character: '🇧🇭', title: 'Bahrain flag' },
        { character: '🇧🇮', title: 'Burundi flag' },
        { character: '🇧🇯', title: 'Benin Republic flag' },
        { character: '🇧🇱', title: 'St. Barthélemy' },
        { character: '🇧🇲', title: 'Bermuda flag' },
        { character: '🇧🇳', title: 'Brunei flag' },
        { character: '🇧🇴', title: 'Bolivia flag' },
        { character: '🇧🇶', title: 'Caribbean Netherlands flag' },
        { character: '🇧🇷', title: 'Brazil flag' },
        { character: '🇧🇸', title: 'Bahamas flag' },
        { character: '🇧🇹', title: 'Bhutan flag' },
        { character: '🇧🇻', title: 'Bouvet Island flag' },
        { character: '🇧🇼', title: 'Botswana flag' },
        { character: '🇧🇾', title: 'Belarus flag' },
        { character: '🇧🇿', title: 'Belize flag' },
        { character: '🇨🇦', title: 'Canada flag' },
        { character: '🇨🇨', title: 'Cocos Islands flag' },
        { character: '🇨🇩', title: 'Congo DR flag' },
        { character: '🇨🇫', title: 'CAR (Central African Republic) flag' },
        { character: '🇨🇬', title: 'Congo Brazzaville flag' },
        { character: '🇨🇭', title: 'Switzerland flag' },
        { character: '🇨🇮', title: 'Côte d’Ivoire flag' },
        { character: '🇨🇰', title: 'Cook Islands flag' },
        { character: '🇨🇱', title: 'Chile flag' },
        { character: '🇨🇲', title: 'Cameroun flag' },
        { character: '🇨🇳', title: 'China flag' },
        { character: '🇨🇴', title: 'Columbia flag' },
        { character: '🇨🇵', title: 'Clipperton Island flag' },
        { character: '🇨🇷', title: 'Costa Rica flag' },
        { character: '🇨🇺', title: 'Cuba flag' },
        { character: '🇨🇻', title: 'Cape Verde Island flag' },
        { character: '🇨🇼', title: 'Curaçao flag' },
        { character: '🇨🇽', title: 'Christmas Island flag' },
        { character: '🇨🇾', title: 'Cyprus flag' },
        { character: '🇩🇪', title: 'Germany flag' },
        { character: '🇩🇬', title: 'Diego Garcia flag' },
        { character: '🇩🇯', title: 'Djibouti flag' },
        { character: '🇩🇰', title: 'Denmark flag' },
        { character: '🇩🇲', title: 'Dominica flag' },
        { character: '🇩🇴', title: 'Dominican Republic flag' },
        { character: '🇩🇿', title: 'Algeria flag' },
        { character: '🇪🇦', title: 'Ceuta &amp; Melilla flag' },
        { character: '🇪🇨', title: 'Ecuador flag' },
        { character: '🇪🇪', title: 'Estonia' },
        { character: '🇪🇬', title: 'Egypt flag' },
        { character: '🇪🇭', title: 'Western Sahara flag' },
        { character: '🇪🇷', title: 'Eritrea flag' },
        { character: '🇪🇸', title: 'Spain flag' },
        { character: '🇪🇹', title: 'Ethiopia flag' },
        { character: '🇪🇺', title: 'EU flag' },
        { character: '🇫🇮', title: 'Finland flag' },
        { character: '🇫🇯', title: 'Fiji flag' },
        { character: '🇫🇰', title: 'Falkland Island flag' },
        { character: '🇫🇲', title: 'Micronesia flag' },
        { character: '🇫🇴', title: 'Faroe Island flag' },
        { character: '🇫🇷', title: 'France flag' },
        { character: '🇬🇦', title: 'Gabon flag' },
        { character: '🇬🇧', title: 'United Kingdom flag' },
        { character: '🇬🇩', title: 'Grenada flag' },
        { character: '🇬🇪', title: 'Georgia flag' },
        { character: '🇬🇫', title: 'French Guiana flag' },
        { character: '🇬🇬', title: 'Guernsey flag' },
        { character: '🇬🇭', title: 'Ghana flag' },
        { character: '🇬🇮', title: 'Gibraltar flag' },
        { character: '🇬🇱', title: 'Greenland flag' },
        { character: '🇬🇲', title: 'Gambia flag' },
        { character: '🇬🇳', title: 'Guinea flag' },
        { character: '🇬🇵', title: 'Guadeloupe flag' },
        { character: '🇬🇶', title: 'Equitorial Guinea flag' },
        { character: '🇬🇷', title: 'Greece flag' },
        { character: '🇬🇸', title: 'South Georgia &amp; South Sandwich Islands flag' },
        { character: '🇬🇹', title: 'Guatemala flag' },
        { character: '🇬🇺', title: 'Guam flag'},
        { character: '🇬🇼', title: 'Guinea Bissau flag' },
        { character: '🇬🇾', title: 'Guyana flag' },
        { character: '🇭🇰', title: 'Hong Kong flag' },
        { character: '🇭🇲', title: 'Heard &amp; McDonald Islands flag' },
        { character: '🇭🇳', title: 'Honduras flag' },
        { character: '🇭🇷', title: 'Croatia flag' },
        { character: '🇭🇹', title: 'Haiti flag' },
        { character: '🇭🇺', title: 'Hungary flag' },
        { character: '🇮🇨', title: 'Canary Islands flag' },
        { character: '🇮🇩', title: 'Indonesia flag' },
        { character: '🇮🇪', title: 'Island flag' },
        { character: '🇮🇱', title: 'Israel flag' },
        { character: '🇮🇲', title: 'Isle of Man flag' },
        { character: '🇮🇳', title: 'India flag' },
        { character: '🇮🇴', title: 'British Indian Ocean Territory flag' },
        { character: '🇮🇶', title: 'Iraq flag' },
        { character: '🇮🇷', title: 'Iran flag' },
        { character: '🇮🇸', title: 'Iceland flag' },
        { character: '🇮🇹', title: 'Italy flag' },
        { character: '🇯🇪', title: 'Jersey flag' },
        { character: '🇯🇲', title: 'Jamaica flag' },
        { character: '🇯🇴', title: 'Jordan flag' },
        { character: '🇯🇵', title: 'Japan flag' },
        { character: '🇰🇪', title: 'Kenya flag' },
        { character: '🇰🇬', title: 'Kyrgyzstan flag' },
        { character: '🇰🇭', title: 'Cambodia flag' },
        { character: '🇰🇮', title: 'Kiribati flag' },
        { character: '🇰🇲', title: 'Comoros Island flag' },
        { character: '🇰🇳', title: 'St. Kitts &amp; Nevis Flag' },
        { character: '🇰🇵', title: 'North Korea flag' },
        { character: '🇰🇷', title: 'South Korea flag' },
        { character: '🇰🇼', title: 'Kuwait flag' },
        { character: '🇰🇾', title: 'Cayman Island flag' },
        { character: '🇰🇿', title: 'Kazakhstan flag' },
        { character: '🇱🇦', title: 'Laos flag' },
        { character: '🇱🇧', title: 'Lebanon flag' },
        { character: '🇱🇨', title: 'St. Lucia flag' },
        { character: '🇱🇮', title: 'Liechtenstein flag' },
        { character: '🇱🇰', title: 'Sri Lanka flag' },
        { character: '🇱🇷', title: 'Liberia flag' },
        { character: '🇱🇸', title: 'Lesotho flag' },
        { character: '🇱🇹', title: 'LIthuania flag' },
        { character: '🇱🇻', title: 'Latvia flag' },
        { character: '🇱🇾', title: 'Libya flag' },
        { character: '🇲🇦', title: 'Morocco flag' },
        { character: '🇲🇨', title: 'Monaco flag' },
        { character: '🇲🇩', title: 'Moldova flag' },
        { character: '🇲🇪', title: 'Montenegro flag' },
        { character: '🇲🇬', title: 'Montenegro flag' },
        { character: '🇲🇭', title: 'Marshall Islands flag' },
        { character: '🇲🇰', title: 'North Macedonia flag' },
        { character: '🇲🇱', title: 'Mali flag' },
        { character: '🇲🇲', title: 'Myanmar flag' },
        { character: '🇲🇳', title: 'Mongolia flag' },
        { character: '🇲🇴', title: 'Macao flag' },
        { character: '🇲🇵', title: 'Northern Mariana Islands flag' },
        { character: '🇲🇶', title: 'Marthinique flag' },
        { character: '🇲🇷', title: 'Mauritania flag' },
        { character: '🇲🇸', title: 'Montserrat flag' },
        { character: '🇲🇹', title: 'Malta flag' },
        { character: '🇲🇺', title: 'Mauritius flag' },
        { character: '🇲🇻', title: 'Maldives flag' },
        { character: '🇲🇼', title: 'Malawi flag' },
        { character: '🇲🇽', title: 'Mexico flag' },
        { character: '🇲🇾', title: 'Malaysia flag' },
        { character: '🇲🇿', title: 'Mozambique flag' },
        { character: '🇳🇦', title: 'Namibia flag' },
        { character: '🇳🇨', title: 'New Caledonia flag' },
        { character: '🇳🇪', title: 'Niger flag' },
        { character: '🇳🇫', title: 'Norfolk Island flag' },
        { character: '🇳🇬', title: 'Nigeria flag' },
        { character: '🇳🇮', title: 'Nicaragua flag' },
        { character: '🇳🇱', title: 'Netherlands flag' },
        { character: '🇳🇴', title: 'Norway flag' },
        { character: '🇳🇵', title: 'Nepal flag' },
        { character: '🇳🇷', title: 'Nauru flag' },
        { character: '🇳🇺', title: 'Niue flag' },
        { character: '🇳🇿', title: 'New Zealand flag' },
        { character: '🇴🇲', title: 'Oman flag' },
        { character: '🇵🇦', title: 'Panama flag' },
        { character: '🇵🇪', title: 'Peru flag' },
        { character: '🇵🇫', title: 'French Polynesia flag' },
        { character: '🇵🇬', title: 'Papua New Guinea flag' },
        { character: '🇵🇭', title: 'Philipines flag' },
        { character: '🇵🇰', title: 'Pakistan flag' },
        { character: '🇵🇱', title: 'Poland flag' },
        { character: '🇵🇲', title: 'St. Pierre &amp; Miquelon' },
        { character: '🇵🇳', title: 'Pitcairn Islands flag' },
        { character: '🇵🇷', title: 'Puerto Rico flag' },
        { character: '🇵🇸', title: 'Palestinian Territories flag' },
        { character: '🇵🇹', title: 'Portugal flag' },
        { character: '🇵🇼', title: 'Palau flag' },
        { character: '🇵🇾', title: 'Paraguay flag' },
        { character: '🇶🇦', title: 'Qatar flag' },
        { character: '🇷🇪', title: 'Réunion flag' },
        { character: '🇷🇸', title: 'Serbia flag' },
        { character: '🇷🇺', title: 'Russia flag' },
        { character: '🇷🇼', title: 'Rwanda flag' },
        { character: '🇸🇦', title: 'Saudi Arabia flag' },
        { character: '🇸🇧', title: 'Solomon Islands' },
        { character: '🇸🇨', title: 'Seychelles flag' },
        { character: '🇸🇩', title: 'Sudan flag' },
        { character: '🇸🇪', title: 'Sweeden flag' },
        { character: '🇸🇬', title: 'Singapore flag' },
        { character: '🇸🇭', title: 'St. Helena flag' },
        { character: '🇸🇮', title: 'Slovenia flag' },
        { character: '🇸🇯', title: 'Svalbard &amp; Jan Mayen' },
        { character: '🇸🇰', title: 'Slovakia flag' },
        { character: '🇸🇱', title: 'Sierra Leone flag' },
        { character: '🇸🇲', title: 'San Marino flag' },
        { character: '🇸🇳', title: 'Senegal flag' },
        { character: '🇸🇴', title: 'Somalia flag' },
        { character: '🇸🇷', title: 'Suriname flag' },
        { character: '🇸🇸', title: 'South Sudan flag' },
        { character: '🇸🇹', title: 'São Tomé &amp; Príncipe flag' },
        { character: '🇸🇻', title: 'El Salvador flag' },
        { character: '🇸🇽', title: 'Saint Maarten flag' },
        { character: '🇸🇾', title: 'Syria flag' },
        { character: '🇸🇿', title: 'Eswatini (Swaziland) flag' },
        { character: '🇹🇦', title: 'Tristan da Cunha flag' },
        { character: '🇹🇨', title: 'Turks &amp; Caicos Islands flag' },
        { character: '🇹🇩', title: 'Chad flag' },
        { character: '🇹🇫', title: 'French Southern Territories flag' },
        { character: '🇹🇬', title: 'Togo flag' },
        { character: '🇹🇭', title: 'Thailand flag' },
        { character: '🇹🇯', title: 'Tajikistan flag' },
        { character: '🇹🇰', title: 'Tokelau flag' },
        { character: '🇹🇱', title: 'Timor-Leste flag' },
        { character: '🇹🇲', title: 'Turkmenistan flag' },
        { character: '🇹🇳', title: 'Tunisia flag' },
        { character: '🇹🇴', title: 'Tonga flag' },
        { character: '🇹🇷', title: 'Turkey flag' },
        { character: '🇹🇹', title: 'Trinidad and Tobago flag' },
        { character: '🇹🇻', title: 'Tuvalu flag' },
        { character: '🇹🇼', title: 'Taiwan flag' },
        { character: '🇹🇿', title: 'Tanzania flag' },
        { character: '🇺🇦', title: 'Ukraine flag' },
        { character: '🇺🇬', title: 'Uganda flag' },
        { character: '🇺🇲', title: 'US Outlying Islands flag' },
        { character: '🇺🇳', title: 'United Nations (UN) flag' },
        { character: '🇺🇸', title: 'United State flag' },
        { character: '🇺🇾', title: 'Uruguay flag' },
        { character: '🇺🇿', title: 'Uzbekistan flag' },
        { character: '🇻🇦', title: 'Vatical City flag' },
        { character: '🇻🇨', title: 'St. Vincent &amp; Grenadines flag' },
        { character: '🇻🇪', title: 'Venezuela flag' },
        { character: '🇻🇬', title: 'British Virgin Islands flag' },
        { character: '🇻🇮', title: 'U.S. Virgin Islands' },
        { character: '🇻🇮', title: 'Vietnam flag' },
        { character: '🇻🇺', title: 'Vanuatu flag' },
        { character: '🇼🇫', title: 'Wallis &amp; Futuna flag' },
        { character: '🇼🇸', title: 'Samoa flag' },
        { character: '🇽🇰', title: 'Kosovo flag' },
        { character: '🇾🇪', title: 'Yemen flag' },
        { character: '🇾🇹', title: 'Mayotte flag' },
        { character: '🇿🇦', title: 'South Africa flag' },
        { character: '🇿🇲', title: 'Zambia flag' },
        { character: '🇿🇼', title: 'Zimbabwe flag' },
        { character: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', title: 'England flag' },
        { character: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', title: 'Scotland flag' },
        { character: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', title: 'Wales flag' }
    ]);
}

// Plugins to include in the build.
const plugins = [
	Essentials,
	Autoformat,
	Bold,
	Italic,
	Underline,
	Strikethrough,
	Code,
	BlockQuote,
	Heading,
	Indent,
	Link,
	List,
	Paragraph,
	PasteFromOffice,
	PastePlainText,
	SourceEditing,
	Alignment,
	RemoveFormat,
	TextTransformation,
    SpecialCharacters,
    Mention,
    SpecialCharactersEmoji,
    SpecialCharactersCurrency,
    SpecialCharactersText,
    Clipboard,
    PasteFromMarkdownExperimental
];

ClassicEditor.builtinPlugins = [ ...plugins,
	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	LinkImage,
    CKFinder,
	Table,
    TableProperties,
    TableCellProperties,
	TableToolbar,
	UploadAdapter,
	CloudServices,
	EasyImage,
	MediaEmbed,
    CodeBlock,
    ResizableHeight,
    FullScreen
];

InlineEditor.builtinPlugins = [ ...plugins, Markdown ];

// Editor configuration.
ClassicEditor.defaultConfig = {
    licenseKey: 'GPL',
	toolbar: {
		items: [
			'heading', 'alignment', '|', 'bold', 'italic', '|',
			'insertTable', 'link', 'bulletedList', 'numberedList', '|', 'undo', 'redo',
			'|', 'blockQuote', 'specialCharacters', 'removeFormat', 'sourceEditing', 'codeBlock', 'fullScreen'
		]
	},
	image: {
		toolbar: [
			'imageTextAlternative',
			// 'imageStyle:full',
			'imageStyle:side'
		]
	},
	table: {
		contentToolbar: [
			'tableColumn', 'tableRow', 'mergeTableCells',
			'tableProperties', 'tableCellProperties'
		]
	},
    codeBlock: {
        languages: [
            // Do not render the CSS class for the plain text code blocks.
            { language: 'plaintext', label: 'Plain text', class: '' },
        ]
    },
	// This value must be kept in sync with the language defined in webpack.config.js.
	language: 'en'
};

// Editor configuration.
InlineEditor.defaultConfig = {
    licenseKey: 'GPL',
	toolbar: {
		items: [
			'heading',
			'|',
			'bold',
			'italic',
			'link',
			'|',
			'bulletedList',
			'numberedList',
			'|',
			'blockQuote',
            'specialCharacters',
			'removeFormat',
            'codeBlock',
            'fullScreen'
		]
	},
	// This value must be kept in sync with the language defined in webpack.config.js.
	language: 'en'
};

export default {
	ClassicEditor, InlineEditor
};
